import { Category } from '../types/product'
import { FolderIcon } from '@heroicons/react/24/outline'

interface CategorySidebarProps {
  categories: Category[]
  loading: boolean
  selectedCategory: Category | null
  onCategorySelect: (category: Category) => void
  collapsed?: boolean
}

const CategorySidebar = ({ 
  categories, 
  loading, 
  selectedCategory, 
  onCategorySelect,
  collapsed = false
}: CategorySidebarProps) => {
  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header - Only show when not collapsed */}
      {!collapsed && (
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Categories</h2>
          <p className="text-sm text-gray-600 mt-1">
            Select a category to view products
          </p>
        </div>
      )}

      {/* Categories List */}
      <div className="flex-1 overflow-y-auto">
        {categories.length === 0 ? (
          <div className={`${collapsed ? 'px-2' : 'px-6'} py-8 text-center`}>
            <div className="mx-auto h-12 w-12 text-gray-400">
              <FolderIcon className="h-12 w-12" />
            </div>
            {!collapsed && (
              <>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No categories</h3>
                <p className="mt-1 text-sm text-gray-500">
                  No categories available at the moment
                </p>
              </>
            )}
          </div>
        ) : (
          <div className={`${collapsed ? 'px-2' : 'px-3'} py-2`}>
            {categories.map((category) => {
              const isSelected = selectedCategory?.id === category.id
              return (
                <button
                  key={category.id}
                  onClick={() => onCategorySelect(category)}
                  className={`w-full text-left rounded-lg mb-2 transition-colors ${
                    collapsed ? 'px-2 py-3' : 'px-3 py-3'
                  } ${
                    isSelected
                      ? 'bg-primary-100 text-primary-900 border border-primary-200'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                  title={collapsed ? category.name : undefined}
                >
                  <div className="flex items-center">
                    <FolderIcon className={`h-5 w-5 ${collapsed ? 'mx-auto' : 'mr-3'} flex-shrink-0`} />
                    {!collapsed && (
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">
                          {category.name}
                        </p>
                        {category.products && category.products.length > 0 && (
                          <p className="text-xs text-gray-500 mt-1">
                            {category.products.length} products
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      {!collapsed && (
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="text-xs text-gray-500 text-center">
            {categories.length} categories available
          </div>
        </div>
      )}
    </div>
  )
}

export default CategorySidebar

